import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAllTournaments, startTournamentRun } from "../api/api_funcs";
import { useRemoteControl, useRemotePage } from "../remote/RemoteControlContext";
import { useRemoteSelectionScroll } from "../remote/useRemoteSelectionScroll";

function orderedGames(tournament) {
  const games = tournament.games || [];
  const byId = new Map(games.map((game) => [game.id, game]));
  const ordered = (tournament.games_order_ids || []).map((id) => byId.get(id)).filter(Boolean);
  const orderedIds = new Set(ordered.map((game) => game.id));
  return [...ordered, ...games.filter((game) => !orderedIds.has(game.id))];
}

function TournamentCard({ tournament, starting, onStart, remoteFocused = false }) {
  const games = orderedGames(tournament);
  const visibleGames = games.slice(0, 4);

  return (
    <article data-remote-focused={remoteFocused ? "true" : undefined} className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border bg-slate-900/70 shadow-2xl shadow-black/20 backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:border-indigo-400/40 hover:shadow-indigo-950/40 ${remoteFocused ? "-translate-y-1 border-cyan-300 ring-4 ring-cyan-300/20" : "border-white/10"}`}>
      <div className="relative grid h-44 grid-cols-2 overflow-hidden bg-gradient-to-br from-indigo-900 via-violet-900 to-fuchsia-900">
        {visibleGames.length ? visibleGames.map((game, index) => (
          <div key={game.id} className={`relative overflow-hidden ${visibleGames.length === 1 ? "col-span-2" : ""}`}>
            {game.cover_image ? (
              <img src={game.cover_image} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
            ) : (
              <div className={`h-full w-full ${index % 2 ? "bg-fuchsia-500/15" : "bg-indigo-500/15"}`} />
            )}
            <div className="absolute inset-0 bg-slate-950/15" />
          </div>
        )) : (
          <div className="col-span-2 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="h-16 w-16 text-white/25" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M8 6h8a5 5 0 0 1 4.8 6.4l-1.1 3.8a2.5 2.5 0 0 1-4.2 1.1L13.8 16h-3.6l-1.7 1.3a2.5 2.5 0 0 1-4.2-1.1l-1.1-3.8A5 5 0 0 1 8 6Z"/><path d="M7 10v4M5 12h4M16.5 11.5h.01M18.5 13.5h.01"/></svg>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/15 to-transparent" />
        <div className="absolute left-5 top-5 flex gap-2">
          {tournament.is_motion_tournament && <span className="rounded-full border border-cyan-300/30 bg-cyan-950/75 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-cyan-200 backdrop-blur">Motion</span>}
          <span className="rounded-full border border-white/20 bg-slate-950/70 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-200 backdrop-blur">{games.length} {games.length === 1 ? "round" : "rounds"}</span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-white">{tournament.name}</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-200">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />{tournament.score_mode}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-fuchsia-500/10 px-3 py-1.5 text-xs font-semibold text-fuchsia-200">
              <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-400" />{tournament.team_mode === "Team" ? "Team battle" : "Solo competition"}
            </span>
          </div>
        </div>

        <div className="mt-6 flex-1">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Round lineup</div>
          <div className="flex items-center">
            {games.slice(0, 5).map((game, index) => (
              <div key={game.id} className={`${index ? "-ml-2" : ""} relative h-10 w-10 overflow-hidden rounded-xl border-2 border-slate-900 bg-slate-800 shadow-lg`} title={game.title}>
                {game.cover_image ? <img src={game.cover_image} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center text-xs font-black text-slate-400">{index + 1}</span>}
              </div>
            ))}
            {games.length > 5 && <span className="-ml-2 flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-900 bg-slate-700 text-xs font-bold text-white">+{games.length - 5}</span>}
            <p className="ml-3 min-w-0 truncate text-sm text-slate-400">{games.map((game) => game.title).join(" · ") || "No games selected"}</p>
          </div>
        </div>

        <div className="mt-6 flex gap-3 border-t border-white/10 pt-5">
          <button
            type="button"
            disabled={Boolean(starting)}
            onClick={() => onStart(tournament.id)}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 px-5 py-3 font-bold text-slate-950 shadow-lg shadow-emerald-500/15 transition hover:-translate-y-0.5 hover:shadow-emerald-500/25 disabled:cursor-wait disabled:opacity-50"
          >
            {starting === tournament.id ? (
              <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-950/30 border-t-slate-950" />Preparing…</>
            ) : (
              <><svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="m8 5 11 7-11 7V5Z" /></svg>Start tournament</>
            )}
          </button>
          <Link aria-label={`Edit ${tournament.name}`} title="Edit tournament" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/15 text-slate-300 transition hover:border-fuchsia-400/50 hover:bg-fuchsia-500/10 hover:text-white" to={`/tournaments/${tournament.id}/edit`}>
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg>
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function TournamentListPage() {
  const { remoteActive } = useRemoteControl();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    getAllTournaments()
      .then(setItems)
      .catch((requestError) => setError(requestError?.response?.data?.detail || requestError.message))
      .finally(() => setLoading(false));
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => [item.name, item.score_mode, item.team_mode, ...orderedGames(item).map((game) => game.title)]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query));
  }, [items, search]);

  const totalRounds = useMemo(() => items.reduce((total, item) => total + orderedGames(item).length, 0), [items]);
  const teamCount = useMemo(() => items.filter((item) => item.team_mode === "Team").length, [items]);
  const focusedTournament = filteredItems[focusedIndex] || null;
  const tournamentGridRef = useRemoteSelectionScroll(remoteActive, focusedTournament?.id ?? null);

  useEffect(() => {
    setFocusedIndex((current) => Math.max(0, Math.min(current, filteredItems.length - 1)));
  }, [filteredItems.length]);

  async function handleStart(id) {
    setStarting(id);
    setError("");
    try {
      const run = await startTournamentRun(id);
      navigate(`/tournament-runs/${run.id}`);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError.message);
      setStarting(null);
    }
  }

  const moveFocus = (direction) => {
    if (!filteredItems.length) return;
    setFocusedIndex((current) => (current + direction + filteredItems.length) % filteredItems.length);
  };

  useRemotePage({
    id: "tournaments",
    title: "Tournaments",
    subtitle: focusedTournament?.name || "Your saved championships",
    state: { search, focusedTournament: focusedTournament?.name || "", count: filteredItems.length },
    sections: [{ title: "Tournaments", controls: [
      { id: "tournaments.search", type: "text", label: "Search", value: search, maxLength: 100, placeholder: "Tournament or game" },
      { id: "tournaments.previous", type: "button", label: "Previous", disabled: !filteredItems.length },
      { id: "tournaments.next", type: "button", label: "Next", disabled: !filteredItems.length },
      { id: "tournaments.start", type: "button", label: "Start selected", disabled: !focusedTournament || Boolean(starting) },
      { id: "tournaments.edit", type: "button", label: "Edit selected", disabled: !focusedTournament },
      { id: "tournaments.create", type: "button", label: "Build tournament" },
    ] }],
    actions: {
      "tournaments.search": ({ value }) => { setSearch(String(value || "").slice(0, 100)); setFocusedIndex(0); },
      "tournaments.previous": () => moveFocus(-1),
      "tournaments.next": () => moveFocus(1),
      "tournaments.start": () => focusedTournament && handleStart(focusedTournament.id),
      "tournaments.edit": () => focusedTournament && navigate(`/tournaments/${focusedTournament.id}/edit`),
      "tournaments.create": () => navigate("/tournaments/new"),
    },
  });

  return (
    <main className="relative mx-auto w-full max-w-7xl flex-1 overflow-hidden px-4 py-10 text-left sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute -left-40 top-10 -z-10 h-96 w-96 rounded-full bg-indigo-600/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 top-52 -z-10 h-96 w-96 rounded-full bg-fuchsia-600/15 blur-3xl" />

      <section className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-slate-900/95 via-indigo-950/80 to-fuchsia-950/65 p-6 shadow-2xl shadow-black/30 sm:p-9">
        <div className="absolute -right-12 -top-20 h-64 w-64 rounded-full border-[42px] border-white/[0.025]" />
        <div className="relative flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-fuchsia-300/20 bg-fuchsia-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-fuchsia-200">
              Tournament arena
            </div>
            <h1 className="max-w-3xl text-4xl font-black tracking-tight text-white sm:text-5xl">Your championships,<br className="hidden sm:block" /> ready when you are.</h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-300">Launch a saved tournament, bring players into one persistent lobby, and move through every game without reconnecting.</p>
          </div>
          <Link className="group inline-flex shrink-0 items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 px-6 py-4 font-bold text-white shadow-xl shadow-fuchsia-500/20 transition hover:-translate-y-0.5 hover:shadow-fuchsia-500/30" to="/tournaments/new">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/15 text-xl font-light">+</span>
            Build tournament
            <span className="transition group-hover:translate-x-1">→</span>
          </Link>
        </div>

        <div className="relative mt-8 grid grid-cols-3 gap-3 border-t border-white/10 pt-6 sm:max-w-xl sm:gap-6">
          <div><div className="text-2xl font-black text-white">{items.length}</div><div className="mt-1 text-xs uppercase tracking-wider text-slate-400">Tournaments</div></div>
          <div><div className="text-2xl font-black text-indigo-300">{totalRounds}</div><div className="mt-1 text-xs uppercase tracking-wider text-slate-400">Total rounds</div></div>
          <div><div className="text-2xl font-black text-fuchsia-300">{teamCount}</div><div className="mt-1 text-xs uppercase tracking-wider text-slate-400">Team events</div></div>
        </div>
      </section>

      <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div><h2 className="text-2xl font-bold text-white">Saved tournaments</h2><p className="mt-1 text-sm text-slate-400">Pick a format and open its player lobby.</p></div>
        {items.length > 0 && (
          <label className="relative block sm:w-80">
            <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-500"><svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg></span>
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search tournaments or games" className="w-full rounded-2xl border border-white/10 bg-slate-900/75 py-3.5 pl-11 pr-4 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-indigo-400/70 focus:ring-4 focus:ring-indigo-500/10" />
          </label>
        )}
      </div>

      {error && <div role="alert" className="mt-6 rounded-2xl border border-red-400/25 bg-red-500/10 px-5 py-4 text-sm text-red-200">{error}</div>}

      {loading ? (
        <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((item) => <div key={item} className="h-[470px] animate-pulse rounded-3xl border border-white/5 bg-white/5" />)}</div>
      ) : filteredItems.length ? (
        <div ref={tournamentGridRef} className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filteredItems.map((item, index) => <TournamentCard key={item.id} tournament={item} starting={starting} onStart={handleStart} remoteFocused={remoteActive && index === focusedIndex} />)}
        </div>
      ) : items.length ? (
        <div className="mt-6 rounded-3xl border border-dashed border-white/15 bg-slate-900/40 px-6 py-16 text-center"><div className="text-xl font-bold text-slate-200">No matching tournaments</div><p className="mt-2 text-sm text-slate-500">Try a tournament name, game title, or scoring mode.</p><button type="button" onClick={() => setSearch("")} className="mt-5 rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/5">Clear search</button></div>
      ) : !error && (
        <div className="mt-6 rounded-[2rem] border border-dashed border-indigo-300/20 bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/5 px-6 py-16 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 shadow-xl shadow-fuchsia-500/20"><svg viewBox="0 0 24 24" className="h-8 w-8 text-white" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M8 6h8a5 5 0 0 1 4.8 6.4l-1.1 3.8a2.5 2.5 0 0 1-4.2 1.1L13.8 16h-3.6l-1.7 1.3a2.5 2.5 0 0 1-4.2-1.1l-1.1-3.8A5 5 0 0 1 8 6Z"/><path d="M7 10v4M5 12h4M16.5 11.5h.01M18.5 13.5h.01"/></svg></div>
          <h3 className="mt-5 text-2xl font-black text-white">Create your first championship</h3>
          <p className="mx-auto mt-2 max-w-md text-slate-400">Choose games, arrange the rounds, and select how players compete and score.</p>
          <Link to="/tournaments/new" className="mt-6 inline-flex rounded-2xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-6 py-3 font-bold text-white shadow-lg shadow-fuchsia-500/20">Build tournament</Link>
        </div>
      )}
    </main>
  );
}
